// Iteration 143 (added scope) -- super_admin Policies admin page.
// Upload or paste a markdown policy, set version + effective date, preview, and
// publish. Publishing a new version auto-supersedes the current one (the API
// sunsets the prior active version). Covers every policy type, incl. child_data.
import { useCallback, useEffect, useMemo, useState } from "react";
import { ALL_POLICY_TYPES, type AnyPolicyType, type PolicyVersionPublic } from "@dugads/shared";
import { useAuth } from "@/lib/auth-context";
import * as api from "@/lib/api";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Upload, ShieldAlert } from "lucide-react";

const POLICY_LABELS: Record<AnyPolicyType, string> = {
  privacy: "Privacy Policy",
  terms: "Terms of Service",
  families: "Families Policy",
  child_data: "Child Data Consent (POPIA)",
};

// Date -> value for <input type="datetime-local"> (local time, minute precision).
function toLocalInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function AdminPoliciesPage() {
  const { user } = useAuth();
  const isSuperAdmin = !!user?.roles.some((r) => r.role_type === "super_admin");

  const [current, setCurrent] = useState<Record<string, PolicyVersionPublic | null>>({});
  const [loading, setLoading] = useState(true);

  const [type, setType] = useState<AnyPolicyType>("child_data");
  const [version, setVersion] = useState("");
  const [effectiveLocal, setEffectiveLocal] = useState(() => toLocalInput(new Date()));
  const [body, setBody] = useState("");
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const loadCurrent = useCallback(async () => {
    setLoading(true);
    const entries = await Promise.all(
      ALL_POLICY_TYPES.map((t) =>
        api
          .getCurrentPolicy(t)
          .then((r) => [t, r.policy] as const)
          .catch(() => [t, null] as const),
      ),
    );
    setCurrent(Object.fromEntries(entries));
    setLoading(false);
  }, []);

  useEffect(() => {
    if (isSuperAdmin) void loadCurrent();
  }, [isSuperAdmin, loadCurrent]);

  const canPublish = useMemo(
    () => version.trim().length > 0 && body.trim().length > 0 && !!effectiveLocal,
    [version, body, effectiveLocal],
  );

  function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setBody(typeof reader.result === "string" ? reader.result : "");
    reader.readAsText(file);
  }

  async function publish() {
    if (!canPublish) return;
    setPublishing(true);
    setError(null);
    setSuccess(null);
    try {
      const effective_at = new Date(effectiveLocal).toISOString();
      const res = await api.publishPolicy(type, { version: version.trim(), body_markdown: body, effective_at });
      setSuccess(`Published ${POLICY_LABELS[type]} version "${res.policy.version}".`);
      setVersion("");
      await loadCurrent();
    } catch (e) {
      setError(e instanceof api.ApiError ? e.message : "Failed to publish policy");
    } finally {
      setPublishing(false);
    }
  }

  if (!isSuperAdmin) {
    return (
      <div
        className="rounded-md border border-destructive/50 bg-destructive/10 p-4 text-destructive"
        data-page="admin-policies"
        data-state="forbidden"
      >
        Only a super admin can manage policies.
      </div>
    );
  }

  const selectClass =
    "flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

  return (
    <div className="flex flex-col gap-6" data-page="admin-policies" data-state={loading ? "loading" : "ready"}>
      <div>
        <h1 className="text-2xl font-semibold">Policies</h1>
        <p className="text-muted-foreground">
          Publish privacy, terms, families, and child-data consent policies. A new version supersedes the current one
          and prompts users to re-consent.
        </p>
      </div>

      {/* Current active versions */}
      <Card data-region="current-policies">
        <CardHeader>
          <CardTitle className="text-base">Current active versions</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-2">
          {ALL_POLICY_TYPES.map((t) => {
            const p = current[t];
            return (
              <div key={t} className="flex items-center justify-between gap-4 border-b border-border py-1.5 text-sm last:border-0" data-policy-type={t}>
                <span className="font-medium">{POLICY_LABELS[t]}</span>
                {p ? (
                  <span className="text-muted-foreground">
                    v{p.version} · effective {new Date(p.effective_at).toLocaleDateString()}
                  </span>
                ) : (
                  <span className="text-amber-600">none published</span>
                )}
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Publish a new version */}
      <Card data-region="publish">
        <CardHeader>
          <CardTitle className="text-base">Publish a new version</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="flex flex-col gap-2">
              <Label htmlFor="pol-type">Policy type</Label>
              <select
                id="pol-type"
                className={selectClass}
                value={type}
                onChange={(e) => setType(e.target.value as AnyPolicyType)}
                data-field="type"
              >
                {ALL_POLICY_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {POLICY_LABELS[t]}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="pol-version">Version</Label>
              <Input
                id="pol-version"
                placeholder="e.g. v1-2026-06-15"
                value={version}
                onChange={(e) => setVersion(e.target.value)}
                data-field="version"
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="pol-effective">Effective from</Label>
              <input
                id="pol-effective"
                type="datetime-local"
                className={selectClass}
                value={effectiveLocal}
                onChange={(e) => setEffectiveLocal(e.target.value)}
                data-field="effective"
              />
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="pol-body">Policy text (markdown)</Label>
              <label className="inline-flex cursor-pointer items-center gap-1.5 text-sm text-primary hover:underline">
                <Upload className="h-4 w-4" />
                Upload .md
                <input type="file" accept=".md,.markdown,text/markdown,text/plain" className="hidden" onChange={onFile} data-field="file" />
              </label>
            </div>
            <textarea
              id="pol-body"
              className="min-h-48 w-full rounded-md border border-input bg-background p-3 font-mono text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              placeholder="Paste the policy markdown, or upload a .md file."
              value={body}
              onChange={(e) => setBody(e.target.value)}
              data-field="body"
            />
          </div>

          {body.trim().length > 0 && (
            <div className="flex flex-col gap-2" data-region="preview">
              <Label>Preview (as users will see it)</Label>
              <article className="prose prose-sm max-h-80 max-w-none overflow-y-auto whitespace-pre-wrap rounded border bg-muted/20 p-3 text-sm">
                {body}
              </article>
            </div>
          )}

          {error && <p className="text-sm text-destructive" data-error>{error}</p>}
          {success && <p className="text-sm text-emerald-600" data-success>{success}</p>}

          {type === "child_data" && (
            <p className="inline-flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-800">
              <ShieldAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              Publishing child-data consent prompts every guardian of a minor player to re-approve before their child can
              use their account. Use legally-approved text in production.
            </p>
          )}

          <div className="flex justify-end">
            <Button onClick={publish} disabled={!canPublish || publishing} data-action="publish">
              {publishing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Publishing...
                </>
              ) : (
                "Publish"
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
